import type { Basket } from "../shop/basket.js";
import { clear, h } from "../ui/dom.js";
import {
  OPTION_LABEL,
  configuredLine,
  defaultSelection,
  describeSelection,
  imagePath,
  loadSelection,
  neighbourPaths,
  saveSelection,
  withOption,
  type KeyValueStore,
  type OptionKind,
  type Selection,
  type View
} from "./model.js";
import type { Configurator } from "./schema.js";

export interface ConfiguratorDeps {
  basket: Basket;
  mediaUrl: (rel: string) => string;
  /** Opens the basket (the enquiry). Optional so the panel can be used on its own. */
  openBasket?: () => void;
  /** Where the last selection is remembered for the tab (default: sessionStorage when available). */
  store?: KeyValueStore;
  /** Skip the door-opening clips (prefers-reduced-motion). */
  reducedMotion?: boolean;
  /** Prefer `.webm` for the reveal clips. */
  supportsWebm?: boolean;
}

interface RadioOption {
  id: string;
  label: string;
  hint?: string;
  swatches?: readonly string[];
}

interface RadioGroup {
  root: HTMLElement;
  set(id: string): void;
}

let uid = 0;

/** A real radio group built from buttons: roving tabindex, arrow keys / Home / End move and select, large tap targets. */
function radioGroup(label: string, kind: string, options: RadioOption[], value: string, onChange: (id: string) => void, className = ""): RadioGroup {
  const labelId = `cfg-${kind}-${uid++}`;
  const buttons = new Map<string, HTMLButtonElement>();
  const root = h("div", { class: `cfg-group ${className}`.trim(), role: "radiogroup", "aria-labelledby": labelId, "data-group": kind });
  const heading = h("h3", { class: "cfg-legend", id: labelId }, label);
  const set = (id: string): void => {
    for (const [oid, b] of buttons) {
      const on = oid === id;
      b.setAttribute("aria-checked", String(on));
      b.tabIndex = on ? 0 : -1;
      b.classList.toggle("is-selected", on);
    }
  };
  const move = (from: string, step: number): void => {
    const ids = options.map((o) => o.id);
    const next = ids[(ids.indexOf(from) + step + ids.length) % ids.length]!;
    set(next);
    buttons.get(next)?.focus();
    onChange(next);
  };
  const list = h("div", { class: "cfg-options" });
  for (const o of options) {
    const b = h(
      "button",
      {
        type: "button",
        class: "cfg-option",
        role: "radio",
        "aria-checked": "false",
        "data-option": `${kind}:${o.id}`,
        onclick: () => {
          set(o.id);
          onChange(o.id);
        },
        onkeydown: (e) => {
          const k = (e as KeyboardEvent).key;
          const step = k === "ArrowRight" || k === "ArrowDown" ? 1 : k === "ArrowLeft" || k === "ArrowUp" ? -1 : 0;
          if (step !== 0) {
            e.preventDefault();
            move(o.id, step);
          } else if (k === "Home" || k === "End") {
            e.preventDefault();
            move(k === "Home" ? options[options.length - 1]!.id : options[0]!.id, k === "Home" ? 1 : -1);
          }
        }
      },
      o.swatches?.length
        ? h("span", { class: "cfg-swatch", "aria-hidden": "true" }, ...o.swatches.map((c) => h("span", { style: `background:${c}` })))
        : null,
      h("span", { class: "cfg-option-text" }, h("span", { class: "cfg-option-name" }, o.label), o.hint ? h("span", { class: "cfg-option-hint" }, o.hint) : null)
    );
    buttons.set(o.id, b);
    list.append(b);
  }
  root.append(heading, list);
  set(value);
  return { root, set };
}

function defaultStore(): KeyValueStore | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * The configurator panel: pick paint, wheels and interior trim; the picture crossfades to the matching image.
 * "Add to enquiry" puts the configured car in the basket as a price-on-request line.
 */
export function configuratorContent(c: Configurator, d: ConfiguratorDeps): HTMLElement {
  const store = d.store ?? defaultStore();
  let sel: Selection = loadSelection(store, c);
  let view: View = "exterior";

  // ---- picture (two stacked images crossfade; the box keeps a fixed 16:9 so nothing jumps)
  const imgA = h("img", { class: "cfg-img is-on", decoding: "async", width: 1280, height: 720, alt: "" });
  const imgB = h("img", { class: "cfg-img", decoding: "async", width: 1280, height: 720, alt: "" });
  const reveal = h("video", { class: "cfg-reveal", muted: true, playsinline: true, "data-testid": "cfg-reveal", hidden: true }) as HTMLVideoElement;
  reveal.muted = true;
  const cityTag = h("span", { class: "cfg-city", "data-testid": "cfg-city", hidden: true });
  const stage = h("div", { class: "cfg-stage", "data-testid": "cfg-stage" }, imgA, imgB, reveal, cityTag);
  let front: HTMLImageElement = imgA;
  let back: HTMLImageElement = imgB;
  let token = 0;
  const altFor = (): string => {
    const t = describeSelection(c, sel);
    return view === "exterior" ? `McLaren in ${t.paint} with ${t.wheels} wheels` : `McLaren cockpit with ${t.trim} interior`;
  };
  const showImage = (animate: boolean): void => {
    const city = c.paints.find((p) => p.id === sel.paint)?.city;
    cityTag.hidden = view !== "exterior" || !city;
    cityTag.textContent = city ? `Shown in ${city}` : "";
    const path = imagePath(c, sel, view);
    stage.dataset.image = path;
    stage.dataset.view = view;
    const url = d.mediaUrl(path);
    const mine = ++token;
    if (!animate) {
      front.src = url;
      front.alt = altFor();
      return;
    }
    const swap = (): void => {
      if (mine !== token) return;
      back.classList.add("is-on");
      front.classList.remove("is-on");
      [front, back] = [back, front];
    };
    back.alt = altFor();
    if (back.getAttribute("src") === url && back.complete && back.naturalWidth > 0) return void swap();
    back.onload = swap;
    back.onerror = swap;
    back.src = url;
  };
  const preload = (): void => {
    const run = (): void => {
      for (const p of [...neighbourPaths(c, sel, view), imagePath(c, sel, view === "exterior" ? "interior" : "exterior")]) {
        const img = new Image();
        img.decoding = "async";
        img.src = d.mediaUrl(p);
      }
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1500 });
    else setTimeout(run, 200);
  };

  // ---- text
  const summary = h("p", { class: "cfg-summary", "data-testid": "cfg-summary" });
  const live = h("div", { class: "sr-only", role: "status", "aria-live": "polite", "data-testid": "cfg-live" });
  const status = h("p", { class: "status cfg-status", role: "status", "data-testid": "cfg-status" });
  const viewBasket = h("button", { type: "button", class: "btn cfg-btn", "data-testid": "cfg-view-basket", hidden: true, onclick: () => d.openBasket?.() }, "View enquiry");

  // ---- controls
  const viewGroup = radioGroup(
    "View",
    "view",
    [
      { id: "exterior", label: "Exterior" },
      { id: "interior", label: "Interior" }
    ],
    view,
    (id) => {
      view = id as View;
      refresh(false, `Showing the ${view}.`);
    },
    "cfg-group--view"
  );
  const groups: Record<OptionKind, RadioGroup> = {
    paint: radioGroup(
      OPTION_LABEL.paint,
      "paint",
      c.paints.map((p) => ({ id: p.id, label: p.name, swatches: [p.hex] })),
      sel.paint,
      (id) => choose("paint", id),
      "cfg-group--paint"
    ),
    wheels: radioGroup(
      OPTION_LABEL.wheels,
      "wheels",
      c.wheels.map((w) => ({ id: w.id, label: w.name, hint: w.description })),
      sel.wheels,
      (id) => choose("wheels", id)
    ),
    trim: radioGroup(
      OPTION_LABEL.trim,
      "trim",
      c.trims.map((t) => ({ id: t.id, label: t.name, hint: t.description, swatches: t.swatches })),
      sel.trim,
      (id) => choose("trim", id)
    )
  };

  const optionName = (kind: OptionKind): string => describeSelection(c, sel)[kind];

  function refresh(animate: boolean, announcement?: string): void {
    const t = describeSelection(c, sel);
    clear(summary);
    summary.append(h("span", { class: "cfg-summary-label" }, "Your configuration: "), h("strong", {}, t.summary));
    viewGroup.set(view);
    for (const k of ["paint", "wheels", "trim"] as const) groups[k].set(sel[k]);
    showImage(animate);
    saveSelection(store, c, sel);
    if (announcement) live.textContent = announcement;
  }

  function choose(kind: OptionKind, id: string): void {
    sel = withOption(c, sel, kind, id);
    // Paint and wheels are seen on the outside, trim on the inside: show the view that changed.
    const next: View = kind === "trim" ? "interior" : "exterior";
    const switched = next !== view;
    view = next;
    status.textContent = "";
    refresh(true, `${OPTION_LABEL[kind]}: ${optionName(kind)}. ${switched ? `Showing the ${view}. ` : ""}Picture updated.`);
    if (kind === "trim") playReveal();
  }

  /** The host opens the door onto the chosen interior (one clip per trim), then the interior picture stays. */
  function playReveal(): void {
    const clip = c.trims.find((t) => t.id === sel.trim)?.reveal;
    if (!clip || d.reducedMotion) return;
    reveal.src = `${d.mediaUrl(clip)}.${d.supportsWebm ? "webm" : "mp4"}`;
    reveal.hidden = false;
    stage.dataset.reveal = sel.trim;
    const done = (): void => {
      reveal.hidden = true;
      reveal.onended = reveal.onerror = null;
      delete stage.dataset.reveal;
    };
    reveal.onended = done;
    reveal.onerror = done;
    void reveal.play().catch(done);
  }

  const addBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary cfg-btn",
      "data-testid": "cfg-add",
      onclick: () => {
        const line = configuredLine(c, sel);
        d.basket.add(line.productId, line.colour, line.size, 1, { detail: line.detail, image: line.image });
        const n = d.basket.count;
        const msg = `Added to your enquiry: ${describeSelection(c, sel).summary}. ${c.priceLabel}. Basket has ${n} item${n === 1 ? "" : "s"}.`;
        status.textContent = `Added to your enquiry (${n} in basket). ${c.priceLabel}.`;
        live.textContent = msg;
        viewBasket.hidden = false;
      }
    },
    "Add to enquiry"
  );
  const resetBtn = h(
    "button",
    {
      type: "button",
      class: "btn cfg-btn",
      "data-testid": "cfg-reset",
      onclick: () => {
        sel = defaultSelection(c);
        view = "exterior";
        status.textContent = "";
        refresh(true, "Configuration reset to the starting choices.");
      }
    },
    "Reset"
  );

  const root = h(
    "div",
    { class: "cfg", "data-testid": "configurator" },
    h("div", { class: "cfg-view" }, stage, viewGroup.root, summary),
    h(
      "div",
      { class: "cfg-controls" },
      h("h2", { "data-autofocus": true, tabindex: -1, class: "cfg-title" }, c.title),
      h("p", { class: "price cfg-price" }, c.priceLabel),
      groups.paint.root,
      groups.wheels.root,
      groups.trim.root,
      h("div", { class: "cfg-actions" }, addBtn, resetBtn, viewBasket),
      status,
      h("p", { class: "muted small" }, "Images are illustrative. Specification and availability on request. Demo only: nothing is sent.")
    ),
    live
  );
  refresh(false);
  preload();
  return root;
}
